import { useCallback, useEffect, useRef, useState, type ChangeEvent, useMemo } from 'react';
import size from 'lodash/size';
import trim from 'lodash/trim';
import toString from 'lodash/toString';
// local imports
import { MIN_NAME_LENGTH } from '../config/params';

function useEditTitle({
  parentTitle,
  renamePending,
  renameFailed,
  updateTitle
}: {
  parentTitle?: string;
  renamePending?: boolean | null;
  renameFailed?: boolean | null;
  updateTitle: (title?: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState(parentTitle);
  const [editing, setEditing] = useState(false);

  // Rename completion: on the `renamePending` true → false edge, a failure reverts the field to the
  // saved title (the error snackbar explains); success just makes sure editing is closed. Guarded by
  // a prev-value ref so it never fires on mount and StrictMode double-runs stay safe.
  const wasPendingRef = useRef(false);
  useEffect(() => {
    if (wasPendingRef.current && !renamePending) {
      if (renameFailed === true) setTitle(parentTitle);
      else setEditing(false);
    }
    wasPendingRef.current = Boolean(renamePending);
  }, [renamePending, renameFailed, parentTitle]);

  // Secondary exit + field sync: an external `parentTitle` change resets the field and exits editing.
  useEffect(() => {
    setTitle(parentTitle);
    setEditing(false);
  }, [parentTitle]);

  // Focus (plain caret, no auto-select) when entering editing.
  useEffect(() => {
    if (editing) inputRef.current?.focus?.();
  }, [editing]);

  const handleRename = useCallback(() => setEditing(true), []);

  const handleTitleChange = useCallback((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    event?.preventDefault();
    setTitle(toString(event?.target?.value));
  }, []);

  const changed = trim(toString(title)) !== toString(parentTitle);
  const canSave = changed && size(trim(toString(title))) >= MIN_NAME_LENGTH;

  const handleSave = useCallback(() => {
    if (canSave) {
      updateTitle(trim(toString(title)));
      setEditing(false);
    }
  }, [canSave, title, updateTitle]);

  const handleCancel = useCallback(() => {
    setTitle(parentTitle);
    setEditing(false);
  }, [parentTitle]);

  return useMemo(() => ({
    inputRef,
    title,
    editing,
    canSave,
    handleRename,
    handleTitleChange,
    handleSave,
    handleCancel
  }), [canSave, editing, handleCancel, handleRename, handleSave, handleTitleChange, title]);
}

export default useEditTitle;
